import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import type { AgencyUser, Lead } from '../types';
import { COVERAGE_LABELS, DRIVERS_LABELS } from '../leadLabels';
import { leadFieldIssues, hasBlockingError, type FieldIssue } from '../leadValidation';

type Draft = Partial<Lead> & { phone: string };

/** A labelled field wrapper (mirrors LeadDetail's layout). */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="lead-field">
      <span className="lead-field-label">{label}</span>
      {children}
    </label>
  );
}

/** Inline validation message beneath a field (same rules as the bot). */
function IssueNote({ issue }: { issue?: FieldIssue }) {
  if (!issue) return null;
  return <span className={`field-issue ${issue.level}`}>{issue.text}</span>;
}

/**
 * Comprehensive manual-lead form: lets an agent enter every field the bot
 * collects (customer, vehicle, driver/coverage, insurance history), auto-fill
 * the vehicle from the Ministry of Transport registry by plate number, and then
 * either just create the lead or create it and immediately compare prices
 * against the insurers (which opens the lead card with the results).
 */
export function AddLeadModal({
  tenantId,
  onClose,
  onCreated,
}: {
  tenantId?: string;
  onClose: () => void;
  onCreated: (phone: string) => void;
}) {
  const [form, setForm] = useState<Draft>({ phone: '' });
  const [busy, setBusy] = useState<null | 'create' | 'compare'>(null);
  const [looking, setLooking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // Agency users for the "assigned rep" picker. Fails silently for non-managers
  // (the field then behaves as a plain free-text input).
  const [users, setUsers] = useState<AgencyUser[]>([]);

  useEffect(() => {
    api
      .listUsers(tenantId)
      .then(setUsers)
      .catch(() => undefined);
  }, [tenantId]);

  // Live field validation (mirrors the bot's rules) shown inline as you type.
  const issues = useMemo(
    () =>
      leadFieldIssues({
        youngest_driver_age: form.youngest_driver_age,
        youngest_driver_license_years: form.youngest_driver_license_years,
        insurance_start_date: form.insurance_start_date,
        car_number: form.car_number,
        national_id: form.national_id,
      }),
    [form],
  );
  const blocking = hasBlockingError(issues);

  function set<K extends keyof Lead>(key: K, value: Lead[K] | undefined) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  const text = (k: keyof Lead) => (form[k] ?? '') as string;
  const num = (k: keyof Lead) => {
    const v = form[k];
    return v === undefined || v === null ? '' : String(v);
  };
  const setNum = (k: keyof Lead, s: string) => set(k, (s === '' ? undefined : Number(s)) as never);
  const boolSel = (k: keyof Lead) => {
    const v = form[k];
    return v === undefined ? '' : v ? 'true' : 'false';
  };
  const setBool = (k: keyof Lead, s: string) => set(k, (s === '' ? undefined : s === 'true') as never);

  /** Assemble the create payload: phone + every non-empty field. */
  function buildPayload(): Draft {
    const phone = (form.phone || '').trim();
    const payload: Draft = { phone };
    (Object.keys(form) as (keyof Draft)[]).forEach((k) => {
      if (k === 'phone') return;
      const v = form[k];
      if (v === undefined || v === null || v === '') return;
      (payload as Record<string, unknown>)[k as string] = v;
    });
    return payload;
  }

  async function lookupCar() {
    const carNumber = (form.car_number || '').trim();
    if (!carNumber) {
      setError('הזן מספר רכב לשליפה.');
      return;
    }
    setLooking(true);
    setError(null);
    setNote(null);
    try {
      const v = await api.lookupVehicle(carNumber, tenantId);
      if (v.lookupFailed) {
        setError('שליפת פרטי הרכב נכשלה (משרד התחבורה אינו זמין כרגע).');
      } else if (!v.found) {
        setError('לא נמצא רכב עם המספר שהוזן במרשם משרד התחבורה.');
      } else {
        setForm((f) => ({
          ...f,
          car_manufacturer: v.manufacturer ?? f.car_manufacturer,
          car_model: v.model ?? f.car_model,
          car_year: v.year ?? f.car_year,
          car_color: v.color ?? f.car_color,
          car_fuel: v.fuel ?? f.car_fuel,
        }));
        setNote(`נמצא רכב: ${[v.manufacturer, v.model, v.year].filter(Boolean).join(' ')} — הפרטים מולאו.`);
      }
    } catch (e) {
      setError(`שליפת פרטי הרכב נכשלה: ${e}`);
    } finally {
      setLooking(false);
    }
  }

  function validPhone(): boolean {
    return (form.phone || '').replace(/\D/g, '').length >= 9;
  }

  async function createLead(): Promise<string | null> {
    try {
      const res = await api.createLead(buildPayload(), tenantId);
      return res.lead.phone;
    } catch (e) {
      const msg = String(e);
      if (msg.includes('409')) {
        setError(
          msg.includes('"archived":true')
            ? 'קיים ליד בארכיון עם מספר זה — ניתן לשחזר אותו מכרטיס הליד.'
            : 'כבר קיים ליד פעיל עם מספר טלפון זה.',
        );
      } else {
        setError(`יצירת הליד נכשלה: ${msg}`);
      }
      return null;
    }
  }

  async function submitCreate() {
    if (!validPhone()) {
      setError('נדרש מספר טלפון תקין (מפתח הליד).');
      return;
    }
    setBusy('create');
    setError(null);
    const phone = await createLead();
    setBusy(null);
    if (phone) onCreated(phone);
  }

  async function submitCompare() {
    if (!validPhone()) {
      setError('נדרש מספר טלפון תקין (מפתח הליד).');
      return;
    }
    setBusy('compare');
    setError(null);
    const phone = await createLead();
    if (!phone) {
      setBusy(null);
      return;
    }
    try {
      await api.runQuote(phone, tenantId);
    } catch {
      // Even if the quote fails, the lead was created; the card shows the reason.
    }
    setBusy(null);
    onCreated(phone);
  }

  const saving = busy !== null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>הוספת ליד ידני</h2>
          <button className="modal-close" onClick={onClose} aria-label="סגור">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="lead-note warn">{error}</div>}
          {note && <div className="lead-note">{note}</div>}
          {blocking && (
            <div className="lead-note warn">
              ⚠ יש שדות עם ערכים שאינם עומדים בכללי החיתום (מסומנים באדום). ניתן להמשיך, אך מומלץ לתקן לפני השוואת מחירים.
            </div>
          )}

          {/* פרטי הליד */}
          <section className="modal-section">
            <h3>פרטי הליד</h3>
            <div className="modal-grid">
              <Field label="טלפון (מפתח) *">
                <input value={form.phone} placeholder="05XXXXXXXX" autoFocus onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </Field>
              <Field label="שם לקוח">
                <input value={text('customer_name')} onChange={(e) => set('customer_name', e.target.value)} />
              </Field>
              <Field label="ת.ז.">
                <input value={text('national_id')} onChange={(e) => set('national_id', e.target.value)} />
                <IssueNote issue={issues.national_id} />
              </Field>
              <Field label="נציג מטפל">
                <input
                  list="agency-users"
                  value={text('assigned_to')}
                  onChange={(e) => set('assigned_to', e.target.value)}
                />
                <datalist id="agency-users">
                  {users.map((u) => (
                    <option key={u.sub} value={u.email}>
                      {u.name ? `${u.name} (${u.email})` : u.email}
                    </option>
                  ))}
                </datalist>
              </Field>
            </div>
          </section>

          {/* פרטי הרכב */}
          <section className="modal-section">
            <h3>פרטי הרכב</h3>
            <div className="modal-grid">
              <Field label="מספר רכב">
                <div className="input-with-btn">
                  <input value={text('car_number')} onChange={(e) => set('car_number', e.target.value)} />
                  <button type="button" className="btn outline sm" onClick={lookupCar} disabled={looking}>
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
                    {looking ? 'שולף…' : 'משרד התחבורה'}
                  </button>
                </div>
                <IssueNote issue={issues.car_number} />
              </Field>
              <Field label="יצרן">
                <input value={text('car_manufacturer')} onChange={(e) => set('car_manufacturer', e.target.value)} />
              </Field>
              <Field label="דגם">
                <input value={text('car_model')} onChange={(e) => set('car_model', e.target.value)} />
              </Field>
              <Field label="שנת ייצור">
                <input type="number" value={num('car_year')} onChange={(e) => setNum('car_year', e.target.value)} />
              </Field>
              <Field label="צבע">
                <input value={text('car_color')} onChange={(e) => set('car_color', e.target.value)} />
              </Field>
              <Field label="דלק">
                <input value={text('car_fuel')} onChange={(e) => set('car_fuel', e.target.value)} />
              </Field>
            </div>
          </section>

          {/* פרטי הנהג והכיסוי */}
          <section className="modal-section">
            <h3>פרטי הנהג והכיסוי</h3>
            <div className="modal-grid">
              <Field label="גיל נהג צעיר">
                <input type="number" value={num('youngest_driver_age')} onChange={(e) => setNum('youngest_driver_age', e.target.value)} />
                <IssueNote issue={issues.youngest_driver_age} />
              </Field>
              <Field label="מגדר נהג צעיר">
                <select value={(form.youngest_driver_gender as string) ?? ''} onChange={(e) => set('youngest_driver_gender', (e.target.value || undefined) as never)}>
                  <option value="">—</option>
                  <option value="male">זכר</option>
                  <option value="female">נקבה</option>
                </select>
              </Field>
              <Field label="ותק רישיון (שנים)">
                <input type="number" value={num('youngest_driver_license_years')} onChange={(e) => setNum('youngest_driver_license_years', e.target.value)} />
                <IssueNote issue={issues.youngest_driver_license_years} />
              </Field>
              <Field label="מספר נהגים">
                <select value={(form.drivers_count as string) ?? ''} onChange={(e) => set('drivers_count', (e.target.value || undefined) as never)}>
                  <option value="">—</option>
                  {Object.entries(DRIVERS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
              <Field label="סוג כיסוי">
                <select value={(form.coverage_type as string) ?? ''} onChange={(e) => set('coverage_type', (e.target.value || undefined) as never)}>
                  <option value="">—</option>
                  {Object.entries(COVERAGE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
              <Field label="תאריך תחילת ביטוח">
                <input type="date" value={text('insurance_start_date')} onChange={(e) => set('insurance_start_date', e.target.value)} />
                <IssueNote issue={issues.insurance_start_date} />
              </Field>
              <Field label="עיר">
                <input value={text('main_driver_city')} onChange={(e) => set('main_driver_city', e.target.value)} />
              </Field>
              <Field label="מיקוד">
                <input value={text('main_driver_zip')} onChange={(e) => set('main_driver_zip', e.target.value)} />
              </Field>
              <Field label="כתובת מלאה">
                <input value={text('main_driver_address')} onChange={(e) => set('main_driver_address', e.target.value)} />
              </Field>
            </div>
          </section>

          {/* היסטוריית ביטוח */}
          <section className="modal-section">
            <h3>היסטוריית ביטוח</h3>
            <div className="modal-grid">
              {([1, 2, 3] as const).map((y) => {
                const k = `prior_coverage_${y}` as keyof Lead;
                return (
                  <Field key={y} label={`רצף - לפני ${y} שנים`}>
                    <select value={num(k)} onChange={(e) => setNum(k, e.target.value)}>
                      <option value="">—</option>
                      <option value="1">מקיף</option>
                      <option value="2">צד ג'</option>
                      <option value="0">לא היה מבוטח</option>
                    </select>
                  </Field>
                );
              })}
              <Field label="מספר תביעות (3 שנים)">
                <input type="number" value={num('prior_claims_count')} onChange={(e) => setNum('prior_claims_count', e.target.value)} />
              </Field>
              <Field label="רכב משועבד">
                <select value={boolSel('is_pledged')} onChange={(e) => setBool('is_pledged', e.target.value)}>
                  <option value="">—</option>
                  <option value="true">כן</option>
                  <option value="false">לא</option>
                </select>
              </Field>
              <Field label="רכב חברה נוסף במשק הבית">
                <select value={boolSel('spouse_company_car')} onChange={(e) => setBool('spouse_company_car', e.target.value)}>
                  <option value="">—</option>
                  <option value="true">כן</option>
                  <option value="false">לא</option>
                </select>
              </Field>
            </div>
          </section>
        </div>

        <div className="modal-footer">
          <button className="btn outline" onClick={onClose} disabled={saving}>ביטול</button>
          <div className="spacer" />
          <button className="btn secondary" onClick={submitCreate} disabled={saving}>
            {busy === 'create' ? 'שומר…' : 'צור ליד'}
          </button>
          <button className="btn" onClick={submitCompare} disabled={saving}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18M18 17V9M13 17V5M8 17v-3" /></svg>
            {busy === 'compare' ? 'משווה מחירים…' : 'צור והשווה מחירים'}
          </button>
        </div>
      </div>
    </div>
  );
}
